import { CompanyContainer } from "components/company/v3/CompanyContainers";
import CompanyLocalState from "components/company/v3/CompanyLocalState";
import {
  CompanyAboutTabEnum,
  CompanyAssetsTabEnum,
  CompanyDiscussionsTabEnum,
  CompanyFinancialsTabEnum,
  CompanyLiquidityTabEnum,
  CompanyMenuSlugEnum,
  CompanyQuotesTabEnum,
} from "components/company/v3/menu/enums";
import type { CompanyMenuSlug } from "components/company/v3/menu/types";
import FiisAboutBox from "components/company/v3/realestatefunds/boxes/FiisAboutBox";
import FiisAssetsBox from "components/company/v3/realestatefunds/boxes/FiisAssetsBox";
import FiisDiscussionsBox from "components/company/v3/realestatefunds/boxes/FiisDiscussionsBox";
import FiisFinancialsBox from "components/company/v3/realestatefunds/boxes/FiisFinancialsBox";
import FiisLiquityBox from "components/company/v3/realestatefunds/boxes/FiisLiquityBox";
import FiisQuotesBox from "components/company/v3/realestatefunds/boxes/FiisQuotesBox";
import FiisCardsContent from "components/company/v3/realestatefunds/contents/about/FiisCardsContent";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import type { Selfie } from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";

export default function useFiisBoxes(selfie: Selfie, tickerSymbol: string | null) {
  const { value: enabledIssuerPageBoxes } = useSpecificSetting("enabledIssuerPageBoxes.fiis");

  if (!tickerSymbol) {
    return {
      orderedMenu: [],
    };
  }

  if (
    !enabledIssuerPageBoxes ||
    (enabledIssuerPageBoxes && Array.isArray(enabledIssuerPageBoxes) && enabledIssuerPageBoxes.length === 0)
  ) {
    const systemDefaultOrder = [
      "ABOUT_BOX",
      "CARDS",
      "ASSETS_BOX",
      "LIQUIDITY_BOX",
      "QUOTES_BOX",
      "FINANCIALS_BOX",
      "DISCUSSIONS_BOX",
    ];

    return {
      orderedMenu: orderMenuBoxesByPreferences(
        selfie,
        tickerSymbol.toUpperCase(),
        systemDefaultOrder as CompanyMenuSlug[],
      ),
    };
  }

  return {
    orderedMenu: orderMenuBoxesByPreferences(
      selfie,
      tickerSymbol.toUpperCase(),
      enabledIssuerPageBoxes as CompanyMenuSlug[],
    ),
  };
}

function orderMenuBoxesByPreferences(viewer: Selfie, tickerSymbol: string, preferredOrder: CompanyMenuSlug[]) {
  if (viewer?.isPremium) {
    const boxes = [
      {
        slug: CompanyMenuSlugEnum.ABOUT,
        box: (
          <CompanyLocalState<CompanyAboutTabEnum> defaultTab={CompanyAboutTabEnum.ABOUT}>
            {(props) => <FiisAboutBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.CARDS,
        box: (
          <CompanyContainer className="xl:h-full">
            <FiisCardsContent />
          </CompanyContainer>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.LIQUIDITY,
        box: (
          <CompanyLocalState<CompanyLiquidityTabEnum> defaultTab={CompanyLiquidityTabEnum.TRADE_HISTORY}>
            {(props) => <FiisLiquityBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.ASSETS,
        box: (
          <CompanyLocalState<CompanyAssetsTabEnum> defaultTab={CompanyAssetsTabEnum.DISTRIBUTION}>
            {(props) => <FiisAssetsBox {...props} isRenderPageDefault={true} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.FINANCIALS,
        box: (
          <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
            {(props) => <FiisFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.QUOTES,
        box: (
          <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
            {(props) => <FiisQuotesBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.DISCUSSIONS,
        box: (
          <CompanyLocalState<CompanyDiscussionsTabEnum> defaultTab={CompanyDiscussionsTabEnum.COMMENTS}>
            {(props) => <FiisDiscussionsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
    ];

    return preferredOrder.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
  }
  const boxes = [
    {
      slug: CompanyMenuSlugEnum.ABOUT,
      box: (
        <CompanyLocalState<CompanyAboutTabEnum> defaultTab={CompanyAboutTabEnum.ABOUT}>
          {(props) => <FiisAboutBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.CARDS,
      box: (
        <CompanyContainer className="xl:h-full">
          <FiisCardsContent />
        </CompanyContainer>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.FINANCIALS,
      box: (
        <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
          {(props) => <FiisFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.QUOTES,
      box: (
        <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
          {(props) => <FiisQuotesBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
  ];

  return preferredOrder.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
}
